'use client';

import type { ReactNode } from 'react';
import { useUserStore } from '../../../auth/UserStoreProvider';
import useIsPrintPreview from '../../../hooks/useIsPrintPreview';
import { useNotificationCtaActions as useAppNotificationCtaActions } from '../../../no-code/components/Notifications/hooks/useNotificationCtaActions';
import { NotificationsSnackbarProvider as BaseNotificationsSnackbarProvider } from './BaseNotificationsSnackbarProvider';

type Props = {
  children: ReactNode;
  topics?: string[];
  loggedInUserId?: string;
};

/**
 * LC App notifications snackbar provider
 *
 * Difference from platform's NotificationsSnackbarProvider:
 *  - The CTAs supported for LC apps are URL, PAGE and EMIT_PAGE_EVENT.
 *
 * We have separated out the providers for each since the onCtaClick handler is different for platform and LC apps.
 * Since in platform, we cannot use LC specific hooks like useGlobalAppStateStore.
 * check the implementation of useNotificationCtaActions of each provider for more details.
 */

function AppNotificationsSnackbarProvider({ children, ...props }: Props) {
  const onCtaClick = useAppNotificationCtaActions();

  return (
    <BaseNotificationsSnackbarProvider onCtaClick={onCtaClick} {...props}>
      {children}
    </BaseNotificationsSnackbarProvider>
  );
}

function AppNotificationsSnackbarProviderContainer(props: { children: ReactNode }) {
  const isPrintPreview = useIsPrintPreview();

  const userData = useUserStore().hooks.useCurrentUserDetails();
  const loggedInUserId = userData?.user?.id;
  const topics = userData?.notificationMqttTopics;

  if (isPrintPreview || (!loggedInUserId && !topics)) {
    return props.children;
  }

  return (
    <AppNotificationsSnackbarProvider {...props} loggedInUserId={loggedInUserId} topics={topics} />
  );
}

export { AppNotificationsSnackbarProviderContainer as AppNotificationsSnackbarProvider };
